import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { useTranslation } from 'react-i18next';

import {
  CompanyDepartmentDto,
  CompanyEmployeeDto,
  CompanyEmployeeRoleDto,
} from '@/shared/api/companies';
import { Notification } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';

import { useDepartments } from '../model/useDepartments';
import {
  useDeactivateEmployee,
  useEmployees,
  useUpdateEmployee,
  useResendEmployeeInvite,
} from '../model/useEmployees';
import { AddEmployeeToCompanyButton } from './AddEmployeeToCompanyButton';

function employeeId(employee: CompanyEmployeeDto): string {
  return employee.user_id ?? employee.userId ?? '';
}

function employeeName(employee: CompanyEmployeeDto): string {
  return [employee.first_name ?? employee.firstName, employee.last_name ?? employee.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function employeeDepartmentId(employee: CompanyEmployeeDto): string {
  return employee.department_id ?? employee.departmentId ?? '';
}

function isEmployeeActive(employee: CompanyEmployeeDto): boolean {
  return employee.is_active ?? employee.isActive ?? true;
}

function departmentId(department: CompanyDepartmentDto): string {
  return department.id;
}

function isDepartmentActive(department: CompanyDepartmentDto): boolean {
  return department.is_active ?? department.isActive ?? true;
}

const roleLabelKey: Record<CompanyEmployeeRoleDto, string> = {
  department_head: 'company:employeeRoles.department_head',
  manager: 'company:employeeRoles.manager',
};

export const UsersOfCompanyList = ({ companyId }: { companyId: string }) => {
  const { t } = useTranslation();
  const employeesQuery = useEmployees(companyId);
  const departmentsQuery = useDepartments(companyId);
  const deactivateMutation = useDeactivateEmployee(companyId);
  const updateMutation = useUpdateEmployee(companyId);
  const resendMutation = useResendEmployeeInvite(companyId);

  const employees = employeesQuery.data ?? [];
  const departments = departmentsQuery.data ?? [];
  const departmentById = new Map(
    departments.map((department) => [departmentId(department), department])
  );
  const activeDepartments = departments.filter(isDepartmentActive);

  const isPending = employeesQuery.isPending || departmentsQuery.isPending;
  const isError = employeesQuery.isError || departmentsQuery.isError;
  const error = employeesQuery.error ?? departmentsQuery.error;

  if (isPending) return <Loader containerSize="medium" />;

  if (isError) {
    return (
      <>
        <NotFoundData
          title={t('company:usersList.usersOfCompanyListNotFoundError')}
        />
        <Notification open={isError} title={error?.message} />
      </>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('company:team.employeesTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('company:team.employeesCaption')}
          </Typography>
        </Box>
        <AddEmployeeToCompanyButton
          companyId={companyId}
          departments={activeDepartments}
          disabled={activeDepartments.length === 0}
        />
      </Stack>

      {employees.length === 0 ? (
        <NotFoundData title={t('company:usersList.usersOfCompanyListNotFound')} />
      ) : (
        <TableContainer
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflowX: 'auto',
          }}
        >
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('company:usersList.table.user')}</TableCell>
                <TableCell>{t('company:usersList.table.role')}</TableCell>
                <TableCell>{t('company:usersList.table.department')}</TableCell>
                <TableCell>{t('company:usersList.table.status')}</TableCell>
                <TableCell align="right">{t('company:usersList.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => {
                const id = employeeId(employee);
                const currentDepartmentId = employeeDepartmentId(employee);
                const department = departmentById.get(currentDepartmentId);
                const active = isEmployeeActive(employee);
                const inviteStatus = employee.invite_status ?? employee.inviteStatus;

                return (
                  <TableRow key={id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography fontWeight={600}>
                          {employeeName(employee) || employee.email || id}
                        </Typography>
                        {employee.email ? (
                          <Typography variant="body2" color="text.secondary">
                            {employee.email}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={employee.role}
                          disabled={updateMutation.isPending || !id || !active}
                          onChange={(event) =>
                            updateMutation.mutate({
                              userId: id,
                              data: {
                                role: event.target.value as CompanyEmployeeRoleDto,
                              },
                            })
                          }
                        >
                          <MenuItem value="manager">
                            {t(roleLabelKey.manager)}
                          </MenuItem>
                          <MenuItem value="department_head">
                            {t(roleLabelKey.department_head)}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={department ? currentDepartmentId : ''}
                          disabled={updateMutation.isPending || !id || !active}
                          displayEmpty
                          onChange={(event) =>
                            updateMutation.mutate({
                              userId: id,
                              data: { department_id: String(event.target.value) },
                            })
                          }
                        >
                          {!department ? (
                            <MenuItem value="" disabled>
                              {t('company:team.departmentUnknown')}
                            </MenuItem>
                          ) : null}
                          {departments.map((departmentOption) => (
                            <MenuItem
                              key={departmentId(departmentOption)}
                              value={departmentId(departmentOption)}
                              disabled={!isDepartmentActive(departmentOption)}
                            >
                              {departmentOption.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          color={active ? 'success' : 'default'}
                          label={active ? t('company:team.active') : t('company:team.inactive')}
                        />
                        {inviteStatus ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(`company:team.inviteStatus.${inviteStatus}`)}
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ReplayOutlinedIcon />}
                          disabled={resendMutation.isPending || !id || inviteStatus !== 'pending'}
                          onClick={() => resendMutation.mutate(id)}
                        >
                          {t('company:team.resendInvite')}
                        </Button>
                        {active ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<PersonOffOutlinedIcon />}
                            disabled={deactivateMutation.isPending || !id}
                            onClick={() => deactivateMutation.mutate(id)}
                          >
                            {t('company:team.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={updateMutation.isPending || !id}
                            onClick={() =>
                              updateMutation.mutate({
                                userId: id,
                                data: { is_active: true },
                              })
                            }
                          >
                            {t('company:team.activate')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Notification
        open={deactivateMutation.isError}
        title={deactivateMutation.error?.message}
      />
      <Notification
        open={resendMutation.isError}
        title={resendMutation.error?.message}
      />
      <Notification
        open={updateMutation.isError}
        title={updateMutation.error?.message}
      />
    </Box>
  );
};
